import React, { Fragment, ReactElement } from 'react';
import { Dispatch } from 'redux';
import { useDispatch, useSelector } from 'react-redux';
import { IntlShape, useIntl } from 'react-intl/dist';

// Components

import Form from '../../../lib/wrappers/Form';
import UniversalInput from '../../../lib/wrappers/UniversalInput';

// Action Creators / Selectors

import { changeData } from '../../../actions/ssap/ssapActionCreators';
import { getSsapData, selectPrimaryTaxFiler, getStateCode } from '../../../reducers/appReducer';

// Utils

import { currencyTools } from '../../../utils/tools/currencyTools';
import { getFrequencyId } from '../../../utils/householdMember/getFrequencyId';
import { normalizeNameOfPerson } from '../../../utils/normalize/normalizeNameOfPerson';
import { preventerTools } from '../../../utils/tools/preventerTools';

// Constants

import { INCOME_TYPES } from '../../../constants/ssap/hhmTypes/incomeAndExpenceTypes';
import { INPUT_TYPES } from '../../../constants/inputs/inputTypes';
import { DROPDOWN_OPTIONS } from '../../../constants/ssap/hhmTypes/dropdownOptions';
import { VALIDATION_TYPES } from '../../../constants/inputs/validationTypes';
import { FREQUENCY_TYPES } from '../../../constants/ssap/hhmTypes/frequencyTypes';
import states from '../../../constants/configs/states';

// Types

import {
    DropdownOption,
    DropdownOptions,
    HouseholdMember,
    Income,
    IncomeDifferenceReason,
    OnChangeArgTypes,
    SsapData,
    SsapPage,
    StateCode
} from '../../../components/ssap/SsapTypes';


const Page3_38_E4 = (props: SsapPage): ReactElement => {

    const { DUMMY, JOB } = INCOME_TYPES;

    const {
        formName
    } = props;

    const dispatch: Dispatch<any> = useDispatch();
    const intlApi: IntlShape = useIntl();

    const data: SsapData = useSelector(getSsapData);
    const stateCode: StateCode = useSelector(getStateCode);
    const primaryTaxFiler: HouseholdMember = useSelector(selectPrimaryTaxFiler);

    const onTaxHouseholdReasonChange = (...args: OnChangeArgTypes): void => {
        const [ updatedPrimaryTaxFiler ] = args;
        const newData: SsapData = { ...data };

        newData.taxHousehold.householdMember =
            newData.taxHousehold.householdMember.map(
                (hhm: HouseholdMember): HouseholdMember => {
                    return hhm.personId === primaryTaxFiler.personId ? updatedPrimaryTaxFiler : hhm;
                }
            );

        dispatch(changeData(newData));
    };

    const getIncomeDiscrepancyFilteredOptions = (dropdownOptions: DropdownOptions, stateCode: StateCode, frequency): DropdownOptions => {
        const {
            IDAHO,
            NEVADA,
            PENNSYLVANIA,
            GEORGIA,
            VIRGINIA
        } = states;

        const {
            MONTHLY,
            YEARLY
        } = FREQUENCY_TYPES;

        const supportedStates = [ IDAHO, NEVADA, PENNSYLVANIA, GEORGIA, VIRGINIA ];
        const supportedFrequencies = [ MONTHLY, YEARLY ];

        const hasDiscrepancyFilter: boolean = frequency
            ? supportedStates.includes(stateCode) && supportedFrequencies.includes(frequency.toUpperCase())
            : false;

        return hasDiscrepancyFilter
            ? dropdownOptions.filter(
                (option: DropdownOption): boolean => option.value !== IncomeDifferenceReason.ENTERED_CORRECTLY
            )
            : dropdownOptions;
    };

    const onIndividualReasonChange = (...args: OnChangeArgTypes): void => {
        const [ , [ householdMemberIndex, incomeIndex ], , [ name, value ] ] = args;

        const newData = { ...data };

        newData.taxHousehold.householdMember =
            newData.taxHousehold.householdMember.map((hhm: HouseholdMember, idx: number): HouseholdMember => {
                return idx === householdMemberIndex
                    ? {
                        ...hhm,
                        incomes: hhm.incomes.map((income: Income, idx: number): Income => {
                            return idx === incomeIndex
                                ? {
                                    ...income,
                                    [ name ]: value
                                }
                                : income;
                        })
                    }
                    : hhm;
            });

        dispatch(changeData(newData));
    };

    const {
        annualTaxIncome: {
            incomeExplanationRequiredIndicator,
            reportedIncome,
            taxHouseholdIncomeDifferenceReason,
        },
    } = preventerTools.provideObject(primaryTaxFiler, {
        annualTaxIncome: {},
    } as any);

    const getIndividualIncomeDiscrepancies = (): Array<ReactElement> => {
        interface ExtendedAndModifiedIncome extends Omit<Income, 'amount' | 'frequency'> {
            amount: string,
            frequency: string | null,
            incomeIndex: number,
            householdMemberIndex: number,
            personName: string
        }

        const incomesWithDiscrepancies: Array<ExtendedAndModifiedIncome> = data
            .taxHousehold
            .householdMember
            .reduce(
                (
                    matchedIncomes: Array<never>,
                    hhm: HouseholdMember,
                    householdMemberIndex: number
                ): Array<ExtendedAndModifiedIncome> => {
                    const { name } = hhm;

                    return [
                        ...matchedIncomes,
                        ...hhm
                            .incomes
                            .reduce(
                                (
                                    incomes: Array<never>,
                                    income: Income,
                                    incomeIndex: number
                                ): Array<ExtendedAndModifiedIncome>  => {

                                    const {
                                        amount,
                                        frequency,
                                        type
                                    } = income;

                                    const formattedAmount: string = currencyTools.getMoneyFormat(amount, true);
                                    const getFormattedFrequency = (): string | null => {
                                        return frequency === FREQUENCY_TYPES.UNSPECIFIED
                                            ? null
                                            : intlApi
                                                .formatMessage({ id: getFrequencyId(frequency) })
                                                .toLowerCase();
                                    };

                                    const personName: string = normalizeNameOfPerson(name);

                                    return (type === DUMMY || type === JOB) && !!income.jobIncomeExplanationRequiredIndicator
                                        ? [
                                            ...incomes,
                                            {
                                                ...income,
                                                incomeIndex,
                                                householdMemberIndex,
                                                amount: formattedAmount,
                                                frequency: getFormattedFrequency(),
                                                personName
                                            }
                                        ]
                                        : incomes;
                                },
                                [ ]
                            )
                    ];
                },
                [ ]
            );

        return incomesWithDiscrepancies.map((income: ExtendedAndModifiedIncome): ReactElement => {

            const {
                amount,
                frequency,
                householdMemberIndex,
                incomeDifferenceReason,
                incomeIndex,
                personName,
                sourceName
            } = income;

            return (
                <Fragment key = { `${income.incomeIndex}_${income.householdMemberIndex}` }>
                    <UniversalInput
                        legend = { {
                            legendText: 'ssap.input.legend.individual_income_discrepancy',
                            dataInstructions: 'instructional_text.income_discrepancy',
                            legendTextReplacements: {
                                amount: <strong>{ amount }</strong>,
                                frequency: <strong>{ frequency }</strong>,
                                personName: <strong>{ personName }</strong>,
                                sourceName
                            }
                        } }
                        label = { {
                            ulClass: [ 'margin-b-20' ],
                            showRequiredIcon: true
                        } }
                        inputData = { {
                            isInputRequired: true,
                            type: INPUT_TYPES.RADIO_BUTTONS,
                            name: 'incomeDifferenceReason',
                            value: incomeDifferenceReason,
                            options: getIncomeDiscrepancyFilteredOptions(
                                DROPDOWN_OPTIONS.INCOME_INDIVIDUAL_DISCREPANCIES,
                                stateCode,
                                frequency
                            ),
                            validationType: VALIDATION_TYPES.CHOSEN,
                            currentData: income,
                            fields: []
                        } }
                        inputActions = { {
                            onFieldChange: onIndividualReasonChange
                        } }
                        indexInArray = { [ householdMemberIndex, incomeIndex ] }
                    />
                </Fragment>
            );
        });
    };

    return (
        <Fragment>
            <div
                className = 'fade-in'
                id = 'iiIncomeDiscrepancy'
            >
                <div className = 'subsection' >
                    <Form { ...{ formName }}>
                        {
                            getIndividualIncomeDiscrepancies()
                        }
                        {
                            incomeExplanationRequiredIndicator &&

                        <UniversalInput
                            legend = { {
                                legendText: 'ssap.input.legend.tax_household_income_discrepancy',
                                dataInstructions: 'instructional_text.income_discrepancy',
                                legendTextReplacements: {
                                    taxHouseholdAnnualAmount: (
                                        <strong>
                                            { currencyTools.getMoneyFormat(reportedIncome, true) }
                                        </strong>
                                    )
                                }
                            } }
                            label = { {
                                ulClass:[ 'margin-b-20' ],
                                showRequiredIcon: true
                            } }
                            inputData = { {
                                isInputRequired: true,
                                type: INPUT_TYPES.RADIO_BUTTONS,
                                name: 'taxHouseholdIncomeDifferenceReason',
                                value: taxHouseholdIncomeDifferenceReason,
                                options: getIncomeDiscrepancyFilteredOptions(
                                    DROPDOWN_OPTIONS.INCOME_TAX_HOUSEHOLD_DISCREPANCIES,
                                    stateCode,
                                    FREQUENCY_TYPES.YEARLY
                                ),
                                validationType: VALIDATION_TYPES.CHOSEN,
                                currentData: primaryTaxFiler,
                                fields: [ 'annualTaxIncome' ]
                            } }
                            inputActions = { {
                                onFieldChange: onTaxHouseholdReasonChange
                            } }
                        />
                        }
                    </Form>
                </div>
            </div>
        </Fragment>
    );
};

export default Page3_38_E4;